import React, { useEffect, useRef, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { api } from '../api/client';
import { tokens } from '../lib/styleTokens';
import { useAuth } from '../hooks/useAuth';
import Sidebar, { findActiveNavLabel, HomeIcon } from './Sidebar';
import ThemeToggle from './ThemeToggle';
import styles from './Layout.module.css';

const MINI_KEY = 'sidebar_mini';

export default function Layout() {
  const location = useLocation();
  const { user, currentWorkspace, switchWorkspace } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [mini, setMini] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem(MINI_KEY) === 'true';
  });
  const [capacityBalance, setCapacityBalance] = useState<number | null>(null);
  const [dateLabel, setDateLabel] = useState('');
  const [pageLabel, setPageLabel] = useState<string | null>(null);
  const [workspaceOpen, setWorkspaceOpen] = useState(false);
  const workspaceRef = useRef<HTMLDivElement>(null);

  const sidebarWidth = isMobile ? 260 : mini ? 64 : 240;

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  useEffect(() => {
    try {
      window.localStorage.setItem(MINI_KEY, String(mini));
    } catch {
      // ignore
    }
  }, [mini]);

  useEffect(() => {
    setPageLabel(findActiveNavLabel(location.pathname));
  }, [location.pathname]);

  useEffect(() => {
    setDateLabel(
      new Date().toLocaleDateString('en-GB', {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      })
    );
  }, []);

  useEffect(() => {
    let cancelled = false;
    api
      .get('/api/capacity/balance')
      .then((res: any) => {
        if (cancelled) return;
        const balance = res?.balance ?? res?.data?.balance;
        if (typeof balance === 'number') {
          setCapacityBalance(balance);
        } else {
          setCapacityBalance(null);
        }
      })
      .catch(() => {
        if (!cancelled) setCapacityBalance(null);
      });
    return () => {
      cancelled = true;
    };
  }, [currentWorkspace?.id]);

  useEffect(() => {
    function onClickOutside(event: MouseEvent) {
      if (workspaceRef.current && !workspaceRef.current.contains(event.target as Node)) {
        setWorkspaceOpen(false);
      }
    }
    if (workspaceOpen) {
      document.addEventListener('mousedown', onClickOutside);
    }
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [workspaceOpen]);

  const workspaces = user?.workspaces ?? [];
  const handleSwitch = (id: string) => async () => {
    setWorkspaceOpen(false);
    await switchWorkspace(id);
  };

  return (
    <div className={styles.layout}>
      {isMobile && (
        <button
          type="button"
          onClick={() => setSidebarOpen((v) => !v)}
          className={styles.mobileMenuBtn}
          aria-label="Toggle menu"
        >
          ☰
        </button>
      )}
      <aside
        className={styles.sidebar}
        style={{
          width: sidebarWidth,
          transform: isMobile && !sidebarOpen ? 'translateX(-100%)' : 'translateX(0)',
        }}
      >
        <Sidebar
          mini={!isMobile && mini}
          onNavigate={() => isMobile && setSidebarOpen(false)}
          onToggleMini={() => setMini((v) => !v)}
          capacityBalance={capacityBalance}
        />
      </aside>
      {isMobile && sidebarOpen && (
        <div
          className={styles.overlay}
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        />
      )}
      <div
        className={styles.main}
        style={{ marginLeft: isMobile ? 0 : sidebarWidth }}
      >
        <header className={styles.header}>
          <div className={styles.breadcrumb}>
            <span className={styles.breadcrumbIcon}>
              <HomeIcon />
            </span>
            <span className={styles.breadcrumbLabel}>{pageLabel ?? 'Dashboard'}</span>
          </div>
          <div className={styles.headerCenter}>
            {workspaces.length > 0 && (
              <div className={styles.workspaceSwitcher} ref={workspaceRef}>
                <button
                  type="button"
                  className={styles.workspaceButton}
                  onClick={() => setWorkspaceOpen((v) => !v)}
                  aria-expanded={workspaceOpen}
                  aria-haspopup="listbox"
                >
                  <span className={styles.workspaceDot} />
                  <span className={styles.workspaceName}>{currentWorkspace?.name ?? 'Select workspace'}</span>
                  {workspaces.length > 1 && (
                    <span className={`${styles.workspaceChevron} ${workspaceOpen ? styles.workspaceChevronOpen : ''}`}>
                      <ChevronDownIcon />
                    </span>
                  )}
                </button>
                {workspaceOpen && workspaces.length > 1 && (
                  <div className={styles.workspaceDropdown} role="listbox">
                    {workspaces.map((w) => (
                      <button
                        key={w.id}
                        type="button"
                        className={`${styles.workspaceOption} ${w.id === currentWorkspace?.id ? styles.workspaceOptionActive : ''}`}
                        role="option"
                        aria-selected={w.id === currentWorkspace?.id}
                        onClick={handleSwitch(w.id)}
                      >
                        <span className={styles.workspaceOptionDot} style={{ background: w.id === currentWorkspace?.id ? tokens.accent : tokens.borderSecondary }} />
                        <span className={styles.workspaceOptionName}>{w.name}</span>
                        <span className={styles.workspaceOptionRole}>{formatRole(w.role)}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
          <div className={styles.headerRight}>
            <ThemeToggle />
            <span className={`${styles.headerItem} ${styles.capacity}`} aria-label="Capacity balance">
              <MenuZapIcon />
              {capacityBalance ?? '—'}
            </span>
            <span className={styles.headerItem}>{dateLabel}</span>
          </div>
        </header>
        <main style={{ padding: isMobile ? '72px 16px 32px' : 32 }}>
          <Outlet />
        </main>
      </div>
    </div>
  );
}

function formatRole(role?: string) {
  if (!role) return '';
  if (role === '3') return 'Owner';
  if (role === '2') return 'Admin';
  if (role === '1') return 'Member';
  return role.charAt(0).toUpperCase() + role.slice(1);
}

function ChevronDownIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

function MenuZapIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
    </svg>
  );
}
